import type { ElliottImpulseProps, ElliottImpulseItem } from '@/components/Shapes/InteractiveShapes/ElliottImpulse';
import { ShapeType } from '@/types/commontypes';
import type { ChartId, ElliottImpulseSharedState, MoreProps } from '@/types/commontypes';
import { chartOrder } from './chartConfig';
import { clampYToChart } from './useShapeActions';
import { getActiveTemplateNode } from './useActiveShapeProps';

export function applyYClampIfNeeded(
  xyValue: [number, number],
  moreProps: MoreProps,
  sharedState: ElliottImpulseSharedState | undefined,
) {
  const shapesChartId = sharedState?.chartId;

  if (!shapesChartId) return xyValue[1];
  if (moreProps.chartId === shapesChartId) return xyValue[1];

  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const chartConfigs = moreProps.chartConfigs as Array<{ id: string; realYDomain?: [number, number] }>;
  const config = chartConfigs?.find(c => c.id === shapesChartId);
  if (!config?.realYDomain) return xyValue[1];

  const [minY, maxY] = config.realYDomain;

  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const currentIdx = chartOrder.indexOf(moreProps.chartId as ChartId);
  const shapesIdx = chartOrder.indexOf(shapesChartId);

  return currentIdx > shapesIdx ? minY : maxY;
}

export const handleHover = (
  hoveredId: string | number | null,
  moreProps: MoreProps | undefined,
  props: ElliottImpulseProps,
) => {
  const { onHover } = props;
  if (onHover) {
    onHover(hoveredId, moreProps);
  }
};

export const handleDoubleClick = (e: React.MouseEvent, moreProps: MoreProps, props: ElliottImpulseProps) => {
  const { sharedState } = props;
  if (!(sharedState?.status === 'started' && sharedState?.type === ShapeType.PATH)) {
    props.onDoubleClick?.(e, moreProps);
  }
};

export function completeOrExtendPath(updatedPoints: [number, number][], props: ElliottImpulseProps) {
  const { elliottImpulses, type, sharedState, chartId } = props;

  const shapeUuid = crypto.randomUUID();
  // Storage-shape 필드(line 등)를 template node에서 spread하여 새 노드에 보존한다.
  // 누락 시 property dialog의 LineOption 등이 undefined.strokeStyle 오류를 일으킨다.
  const lastProps = getActiveTemplateNode(type);
  const newImpulse: ElliottImpulseItem[] = [
    ...elliottImpulses.map(d => ({ ...d, selected: false })),
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    {
      ...lastProps,
      points: updatedPoints,
      selected: true,
      type,
      hidden: false,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      uuid: shapeUuid,
      chartId: sharedState?.chartId || chartId,
    } as unknown as ElliottImpulseItem,
  ];

  return newImpulse;
}

export const handleSelect = (
  e: React.MouseEvent,
  selectedUuid: string,
  moreProps: MoreProps,
  props: ElliottImpulseProps,
) => {
  const { elliottImpulses } = props;

  const newElliottImpulses = elliottImpulses.map(item => ({
    ...item,
    selected: item.uuid === selectedUuid,
  }));

  const { onSelect } = props;
  if (onSelect !== undefined) {
    onSelect(e, newElliottImpulses, moreProps);
  }
};

export const handleDrawLine = (
  _: React.MouseEvent,
  xyValue: unknown,
  moreProps: MoreProps,
  props: ElliottImpulseProps,
) => {
  const { setSharedState, sharedState, chartId, enabled, type } = props;
  const currentPoints = sharedState?.currentPoints;
  // Set limit based on type
  const pointLimit = getImpulsePointLimit(type);
  if (
    !sharedState ||
    sharedState.status !== 'started' ||
    !enabled ||
    (currentPoints && currentPoints.length > pointLimit)
  )
    return;

  const startChartId = sharedState.chartId;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const currentCharts = moreProps.currentCharts as string[];
  const pointerChartId = currentCharts[0];

  // Only draw for shapes created on this chart
  if (chartId !== startChartId) return;

  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const xy = xyValue as [number, number];
  const [x] = xy;
  let y = xy[1];

  // Clamp Y if pointer is on a different chart
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const chartConfigs = moreProps.chartConfigs as Array<{ id: string; realYDomain?: [number, number] }>;
  y = clampYToChart(pointerChartId, startChartId, chartConfigs, chartOrder, y);

  setSharedState?.({
    ...sharedState,
    endXY: [x, y],
  });
};

export const getImpulsePointLimit = (type: ShapeType): number => {
  switch (type) {
    case ShapeType.PATH:
      return 100;
    case ShapeType.ELLIOTT_CORRECTION_ABC:
      return 4;
    case ShapeType.XABCD_PATTERN:
      return 5;
    case ShapeType.ELLIOTT_IMPULSE_12345:
    default:
      return 6;
  }
};
